/* eslint-disable no-nested-ternary */
import styled, { css, keyframes } from 'styled-components';
import { InputSizesType, InputVariantType } from '../ThemeProvider/types';

const errorBounce = keyframes`
 0% {
    transform: translateX(5px);
  }
  25% {
    transform: translateX(-5px);
  }
  50% {
    transform: translateX(5px);
  }
  75% {
    transform: translateX(-5px);
  }
  100% {
    transform: translateX(5px);
  }
`;

const Font = css`
  font-family: 'Mulish', sans-serif;
  font-weight: 400;
  font-style: normal;
`;

export const AddOnContainer = styled.div<{
  hasError?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  size: InputSizesType;
}>`
  padding: ${({ size }) => (size === 'small' ? '4px 0' : '6px 0')};
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px;
  ${({ theme, hasError, disabled, readOnly }) => css`
    height: ${theme.basicIcon.medium.size}px;

    & > * {
      cursor: ${disabled || readOnly ? 'auto' : 'pointer'} !important;
    }

    & svg > path:not([class$='not-color']),
    & svg > rect:not([class$='not-color']),
    & svg > circle:not([class$='not-color']) {
      stroke: ${hasError ? theme.status.error.color : disabled ? theme.grayB8 : theme.gray80};
    }
  `};
`;

export const FixedLabel = styled.span<{
  hasError: boolean;
  size: InputSizesType;
  disabled: boolean;
}>`
  display: inline-block;
  ${Font}
  ${({ theme, hasError, size, disabled }) => css`
    font-size: ${theme.input.sizes[size].fontSize}px;
    line-height: ${theme.input.sizes[size].lineHeight + 4}px;
    color: ${hasError ? theme.status.error.color : disabled ? theme.grayB8 : theme.gray80};
  `}
`;

export const Label = styled(FixedLabel)`
  position: absolute;
  left: 0;
  transition: all 0.17s;
`;

export const Placeholder = styled.span<{
  hide: boolean;
  hasError: boolean;
  size: InputSizesType;
}>`
  position: absolute;
  left: 0;
  top: 0;
  ${Font}
  ${({ theme, hasError, size, hide }) => css`
    font-size: ${theme.input.sizes[size].fontSize}px;
    line-height: ${theme.input.sizes[size].lineHeight}px;
    color: ${hasError ? theme.status.error.color : theme.grayB8};
    display: ${hide ? 'none' : 'inline'};
  `}
`;

const LabelUp = (variant: InputVariantType) => css`
  font-size: 12px;
  line-height: 15px;
  top: ${variant === 'underline' ? 0 : 6}px;
`;

export const Input = styled.input<{
  hasPlaceholder: boolean;
  isOpen: boolean;
  inputSize: InputSizesType;
  variant: InputVariantType;
  pointer: boolean;
  ellipsis: boolean;
}>`
  background: transparent;
  width: calc(100% - 4px);
  padding-bottom: ${({ theme, inputSize }) => theme.input.sizes[inputSize].paddingBottom}px;
  margin-right: 0.5rem;
  color: ${({ theme }) => theme.gray4D};
  position: relative;
  border: none;
  z-index: ${({ theme }) => theme.layers.baseLayer + 1};
  cursor: ${({ pointer }) => (pointer ? 'pointer' : 'auto')};
  text-overflow: ${({ ellipsis }) => ellipsis && 'ellipsis'};
  ${Font}
  ${({ theme, inputSize }) => css`
    font-size: ${theme.input.sizes[inputSize].fontSize}px;
    line-height: ${theme.input.sizes[inputSize].lineHeight}px;
    color: ${theme.gray4D};
  `}

  &::-ms-reveal,
  &::-ms-clear {
    display: none;
  }

  &:not([value='']) {
    ${({ hasPlaceholder, variant }) =>
      !hasPlaceholder &&
      css`
        & ~ ${Label} {
          ${LabelUp(variant)}
        }
      `};
  }

  &:not([readonly]):focus {
    outline: none;
    ${({ hasPlaceholder, isOpen, variant }) =>
      !hasPlaceholder &&
      isOpen !== false &&
      css`
        & ~ ${Label} {
          ${LabelUp(variant)}
        }
      `};
  }

  &:read-only {
    cursor: ${({ pointer }) => (pointer ? 'pointer' : 'auto')};
    outline: none;
    border: none;
    caret-color: transparent;
  }

  &:disabled {
    border-color: ${({ theme }) => theme.grayB8};
    color: ${({ theme }) => theme.grayB8};
    cursor: auto;
  }
`;

export const HelperContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 4px;
  min-height: ${({ theme }) => theme.input.error.containerHeight}px;
`;

export const ErrorMessage = styled.div`
  ${Font}
  ${({ theme }) => css`
    color: ${theme.status.error.color};
    line-height: ${theme.input.error.lineHeight}px;
    font-size: ${theme.input.error.fontSize}px;
  `};
`;

export const HelperMessage = styled(ErrorMessage)<{ disabled: boolean }>`
  color: ${({ theme, disabled }) => (disabled ? theme.grayB8 : theme.gray80)};
`;

export const InputContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  flex: 1;
`;

export const Icon = styled.div<{ disabled: boolean }>`
  & > div {
    cursor: ${({ disabled }) => (disabled ? 'auto' : 'pointer')};
  }
`;

export const BoxContainer = styled.div<{
  variant: InputVariantType;
  hasError: boolean;
  hasPlaceholder: boolean;
  size: InputSizesType;
  disabled: boolean;
  isFocus: boolean;
  isOpen: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  height: 100%;
  width: 100%;
  box-sizing: border-box;

  ${({ hasError, theme }) =>
    hasError &&
    css`
      & ${Input} {
        color: ${theme.status.error.color};
        animation: ${errorBounce} 250ms linear;
        border-color: ${theme.status.error.color};
        &::placeholder {
          color: ${theme.status.error.color};
        }
      }
    `}

  ${({
    theme: { gray80, grayF0, grayB8, gray2D, status, input },
    variant,
    hasError,
    size,
    disabled,
    hasPlaceholder,
    isOpen,
    isFocus,
  }) => {
    const { height, lineHeight, paddingBottom } = input.sizes[size];
    const h = height[variant];
    const top = h - lineHeight - (paddingBottom + 1);
    const center = (h - lineHeight) / 2;
    switch (variant) {
      case 'underline':
        return css`
          border-bottom: 1px solid;
          border-bottom-color: ${hasError
            ? status.error.color
            : disabled
            ? grayB8
            : isFocus
            ? gray2D
            : gray80};

          & ${Label} {
            ${hasPlaceholder || isOpen || isFocus
              ? LabelUp(variant)
              : css`
                  top: ${top}px;
                `};
          }

          & ${Placeholder} {
            top: ${top}px;
          }
        `;
      case 'contained-label':
        return css`
          border: ${hasError || isFocus ? `1px solid` : 'none'};
          border-color: ${hasError ? status.error.color : gray2D};
          border-radius: 8px;
          padding: 0 16px;
          background-color: ${hasError ? input.error.background : grayF0};

          & > ${AddOnContainer} {
            align-self: center;
          }

          & ${Label} {
            ${hasPlaceholder || isOpen
              ? LabelUp(variant)
              : css`
                  top: ${center}px;
                `};
          }

          & ${Placeholder} {
            top: ${top}px;
          }
        `;
      case 'top-aligned-label':
        return css`
          border: ${hasError || isFocus ? `1px solid` : 'none'};
          border-color: ${hasError ? status.error.color : gray2D};
          border-radius: 8px;
          padding: 0 16px;
          background-color: ${hasError ? input.error.background : grayF0};

          & > ${AddOnContainer} {
            align-self: center;
          }

          & ${Placeholder} {
            top: ${center}px;
          }

          & > ${InputContainer} {
            justify-content: center;
            & ${Input} {
              padding-bottom: 0;
            }
          }
        `;
      default:
        return css``;
    }
  }}
`;

export const Container = styled.div<{
  size: InputSizesType;
  variant: InputVariantType;
}>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;

  ${({ theme: { input }, size, variant }) => {
    const h = input.sizes[size].height[variant];
    return css`
      margin-bottom: ${input.spaceBetweenInputs}px;
      & > ${BoxContainer} {
        height: ${h}px;
      }
    `;
  }};
`;
